import { call, put, takeLatest } from 'redux-saga/effects';
import { Api } from '../../api/api';
import { getAppDomain } from '../../utils/domainUtils';
import { saveFranchiseData, loadFranchiseData } from '../../utils/franchiseStorage';
import {
  fetchSalonByDomain,
  fetchSalonByDomainSuccess,
  fetchSalonByDomainFailure,
  selectFranchise,
} from '../slices/salonSlice';
import { setFranchiseInfo, fetchStylists } from '../slices/stylistSlice';
import { setFranchiseInfo as setServiceFranchiseInfo, fetchServices } from '../slices/serviceSlice';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { FranchiseData } from '../../utils/franchiseStorage';

/**
 * Salon Saga
 * Handles salon and franchise API calls
 */

// ============ API Response Interfaces ============

interface SalonApiResponse {
  success: boolean;
  message: string;
  data: {
    id: string;
    name: string;
    type: string;
    description: string;
    owner_name: string;
    state: string;
    country: string;
    zipcode: string;
    phone: string;
    alternative_phone: string;
    address: string;
    features: string;
    logo: string;
    account_id: string;
    domain: string;
    working_hours: Record<string, string>;
    customer_care_number: string;
    capacity: string;
    banner: {
      url: string;
      alt: string;
    };
    payment_type: string;
    template_id: string;
    package_details: {
      plan: string;
      features: string[];
      expiry: string;
    };
    is_enabled: string;
    is_voice_enabled: string;
    payment_perferences: {
      accepted_methods: string[];
      currency: string;
    };
    franchises: FranchiseData[];
  };
  error: null;
}

// ============ Sagas ============

/**
 * Fetch salon by domain saga
 * Calls API to get salon and franchises data
 */
function* fetchSalonByDomainSaga() {
  try {
    // Check if franchise data exists in localStorage
    const storedData = loadFranchiseData();
    
    // Get domain from window.location
    const domain = getAppDomain();
    
    // Call API
    const response: SalonApiResponse = yield call(
      Api.get,
      `/salon-by-domain?domain=${encodeURIComponent(domain)}`
    );
    
    if (response.success && response.data) {
      const franchises = response.data.franchises || [];
      
      // Dispatch success with salon and franchises
      yield put(
        fetchSalonByDomainSuccess({
          domain,
          salon: response.data,
          franchises,
        })
      );
      
      // Priority 1: Check if stored franchise ID matches any franchise in API response
      if (storedData?.franchise) {
        const matchingFranchise = franchises.find(
          (f) => f.id === storedData.franchise.id
        );
        
        if (matchingFranchise) {
          console.log('✅ Auto-selecting stored franchise:', matchingFranchise.name);
          
          // Set franchise info for service and stylist API calls FIRST
          yield put(setServiceFranchiseInfo({
            franchiseId: matchingFranchise.id,
            userId: matchingFranchise.created_by,
            accountId: matchingFranchise.account_id,
          }));
          yield put(setFranchiseInfo({
            franchiseId: matchingFranchise.id,
            userId: matchingFranchise.created_by,
            accountId: matchingFranchise.account_id,
          }));
          
          // Select franchise (selectFranchiseSaga will handle API calls)
          yield put(selectFranchise(matchingFranchise));
          return; // Exit early
        } else {
          console.log('⚠️ Stored franchise not found in API response, clearing localStorage');
        }
      }
      
      // Priority 2: Auto-select if only one franchise
      if (franchises.length === 1) {
        console.log('✅ Auto-selecting single franchise:', franchises[0].name);
        
        // Set franchise info for service and stylist API calls FIRST
        yield put(setServiceFranchiseInfo({
          franchiseId: franchises[0].id,
          userId: franchises[0].created_by,
          accountId: franchises[0].account_id,
        }));
        yield put(setFranchiseInfo({
          franchiseId: franchises[0].id,
          userId: franchises[0].created_by,
          accountId: franchises[0].account_id,
        }));
        
        // Select franchise (selectFranchiseSaga will handle API calls)
        yield put(selectFranchise(franchises[0]));
      }
      // Priority 3: Multiple franchises - user must choose (no action needed)
    } else {
      yield put(
        fetchSalonByDomainFailure(
          response.message || 'Failed to fetch salon data'
        )
      );
    }
  } catch (error: any) {
    console.error('Fetch salon by domain error:', error);
    yield put(
      fetchSalonByDomainFailure(
        error.response?.data?.message ||
          error.message ||
          'Failed to fetch salon data'
      )
    );
  }
}

/**
 * Select franchise saga
 * Saves franchise to localStorage and fetches stylists
 */
function* selectFranchiseSaga(action: PayloadAction<FranchiseData>) {
  try {
    const franchise = action.payload;
    console.log('📍 selectFranchiseSaga triggered for:', franchise.name);
    
    // Get salon data from state (we'll need to select it)
    // For now, we'll create a minimal salon object
    const salonData = {
      id: franchise.salon_id,
      name: franchise.name,
      type: franchise.type,
      description: franchise.description,
      owner_name: franchise.owner_name,
      state: franchise.state,
      country: franchise.country,
      zipcode: franchise.zipcode,
      phone: franchise.phone,
      alternative_phone: franchise.alternative_phone,
      address: franchise.address,
      features: franchise.features,
      logo: typeof franchise.logo === 'string' ? franchise.logo : (franchise.logo?.url || ''),
      account_id: franchise.account_id,
      domain: franchise.domain,
      working_hours: franchise.working_hours,
      customer_care_number: franchise.customer_care_number,
      capacity: String(franchise.capacity),
      banner: franchise.banner,
      payment_type: franchise.payment_type,
      template_id: franchise.template_id,
      package_details: franchise.package_details,
      is_enabled: franchise.is_enabled,
      is_voice_enabled: franchise.is_voice_enabled,
      payment_perferences: franchise.payment_perferences,
    };
    
    // Save to localStorage
    saveFranchiseData({
      franchise,
      salon: salonData,
      selectedAt: new Date().toISOString(),
    });
    console.log('💾 Franchise saved to localStorage');
    
    // Set franchise info for service and stylist API calls
    yield put(setServiceFranchiseInfo({
      franchiseId: franchise.id,
      userId: franchise.created_by,
      accountId: franchise.account_id,
    }));
    yield put(setFranchiseInfo({
      franchiseId: franchise.id,
      userId: franchise.created_by,
      accountId: franchise.account_id,
    }));
    console.log('✅ Franchise info set in service and stylist slices');
    
    // Fetch services and stylists for this franchise
    console.log('🔄 Dispatching fetchServices and fetchStylists...');
    yield put(fetchServices());
    yield put(fetchStylists());
    
    console.log('✅ Franchise selected and APIs triggered:', franchise.name);
  } catch (error: any) {
    console.error('❌ Select franchise error:', error);
  }
}

// ============ Root Saga ============

export default function* salonSaga() {
  yield takeLatest(fetchSalonByDomain.type, fetchSalonByDomainSaga);
  yield takeLatest(selectFranchise.type, selectFranchiseSaga);
}
